import * as React from 'react';

import type ScheduleBoardWorkOrderViewModel from 'ab-socketModels/viewModels/scheduleBoard/scheduleBoardWorkOrder.viewModel';

import { moneyNormalizer } from '@acceligentllc/shared/utils/formatting';
import { calculateManHourAverage, noRevenue, shouldUseAutomaticCalculation } from '@acceligentllc/shared/utils/productionData';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import TooltipElement from './TooltipElement';

interface OwnProps {
	workOrder: Partial<ScheduleBoardWorkOrderViewModel>;
	isAllowedToEditWorkOrders: boolean;
	hasOnlyNonBillableWork: boolean;
}

type Props = OwnProps;

const ProductionDataTooltip: React.FC<Props> = (props) => {
	const {
		workOrder,
		hasOnlyNonBillableWork,
	} = props;

	const totalRevenue = React.useMemo(() => {
		if (workOrder.revenue && workOrder.salesMarkup) {
			return +workOrder.revenue + +workOrder.salesMarkup;
		} else if (workOrder.revenue) {
			return workOrder.revenue;
		}
		return null;
	}, [workOrder.revenue, workOrder.salesMarkup]);

	if (!workOrder.dueDate) {
		return;
	}

	const dueDate = TimeUtils.formatDate(workOrder.dueDate, TimeFormat.DB_DATE_ONLY, TimeFormat.DATE_ONLY);
	const _shouldUseAutomaticCalculation = shouldUseAutomaticCalculation(dueDate);

	const revenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? workOrder.payrollDifferential ?? 0 : 0) + +(workOrder.materialCost ?? 0) - +(workOrder.salesMarkup ?? 0);
	const serviceRevenueSubtractions = (process.env.FTD_DISABLE_PR_DIFF !== 'true' ? workOrder.payrollDifferential ?? 0 : 0) + +(workOrder.materialCost ?? 0);

	return (
		<div className="schedule-board-production-data-tooltip-container">
			<div className="schedule-board-production-data-tooltip-heading">
				<div>PRODUCTION DATA</div>
				{<div className="schedule-board-production-data-tooltip-calculation-explainer">
					{_shouldUseAutomaticCalculation ? '(calculated automatically)' : '(calculated manually)'}
				</div>}
			</div>
			<div>
				<TooltipElement elementName="Revenue"
					elementValue={!hasOnlyNonBillableWork ?
						moneyNormalizer(totalRevenue) :
						noRevenue({ dueDate, hasOnlyNonBillableWork, revenueSubtractions })
					}
				/>
				<TooltipElement elementName="Service Revenue"
					elementValue={!hasOnlyNonBillableWork ?
						moneyNormalizer(workOrder.revenue ?? null) :
						noRevenue({ dueDate, hasOnlyNonBillableWork, revenueSubtractions: serviceRevenueSubtractions })
					}
				/>
				<TooltipElement elementName="Man Hour Average"
					elementValue={_shouldUseAutomaticCalculation ?
						getManHourAverage(
							workOrder.revenue,
							workOrder.jobHours,
							workOrder.shopHours,
							workOrder.travelHours
						)
						: workOrder.manHourAverage ?? '-'}
				/>
				<TooltipElement elementName="Job Hours" elementValue={workOrder.jobHours ?? '-'} />
				<TooltipElement elementName="Shop Hours" elementValue={workOrder.shopHours ?? '-'} />
				<TooltipElement elementName="Travel Hours" elementValue={workOrder.travelHours ?? '-'} />
				{
					process.env.FTD_DISABLE_PR_DIFF !== 'true' &&
					workOrder.payrollDifferential !== null &&
					<TooltipElement elementName="P/R Diff" elementValue={moneyNormalizer(workOrder.payrollDifferential ?? null)} />
				}
			</div>
		</div>
	);

	function getManHourAverage(
		revenue: Nullable<string> | undefined,
		jobHours: Nullable<number> | undefined,
		shopHours: Nullable<number> | undefined,
		travelHours: Nullable<number> | undefined
	) {

		const revenueForCalculation = revenue !== undefined && revenue !== null ?
			Number(revenue)
			: null;

		const mha = calculateManHourAverage(
			revenueForCalculation,
			jobHours ?? 0,
			shopHours ?? 0,
			travelHours ?? 0
		);

		if (mha === null) {
			return 'N/A';
		}

		return moneyNormalizer(mha);
	}
};

export default React.memo(ProductionDataTooltip);
